import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import { useEffect } from "react";
import { Loader } from "lucide-react";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@src/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@src/components/ui/radio-group";
import useZustandStore from "@src/helpers/zustand";
import { cn } from "@src/lib/utils";
import { usePepTypes } from "@src/api/usePepTypes";
import { Skeleton } from "@src/components/ui/skeleton";
import { useKYCAddUserLog, LoggerTypes } from "@src/api/useKYCAddUserLog";
import { WITH_PAYUNICARD } from "@src/utils/consts";
import { useSaveUser } from "@src/components/screens/PEP/use-save-user";

const FormSchema = z.object({
  pep: z.string().refine((v) => v),
});

function Pep() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);
  const pepDetails = usePepTypes();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pep: zData.pep.type,
    },
  });

  const isRedirectedFromPay = location.search.includes(
    "redirected_from=identify-with-payunicard"
  );
  const pep = form.watch("pep");

  useEffect(() => {
    if (pepDetails.data) {
      const defaultCheckedPep = pepDetails.data.find((el) => el.defaultChecked);
      if (defaultCheckedPep && !pep) {
        form.setValue("pep", defaultCheckedPep.type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pepDetails.isSuccess, pep]);

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.PoliticallyExposedPerson,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { onSaveUser, loading } = useSaveUser(() => {
    if (WITH_PAYUNICARD && isRedirectedFromPay) {
      navigate(
        `/${i18n.language}/almost-done?redirected_from=identify-with-payunicard`
      );
    } else {
      navigate(`/${i18n.language}/almost-done`);
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const newData = {
      ...zData,
      pep: {
        ...zData.pep,
        type: data.pep,
      },
    };
    setData(newData);

    if (pep.includes("ConnectedToPEP")) {
      navigate(`/${i18n.language}/pep/details`);
    } else {
      onSaveUser(newData);
    }
  }

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4 gap-4 flex flex-col">
          <TitleAndDesc title={t("pep.title")} />
          <span className="text-xs text-gray-600 font-normal">
            * {t("pep.description")}
          </span>
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="pep"
            render={({ field }) => {
              return (
                <FormItem>
                  <RadioGroup
                    className="rounded-lg border border-solid border-gray-200 bg-white gap-0"
                    onValueChange={(e) => {
                      field.onChange(e);
                    }}
                    value={field.value}
                  >
                    {pepDetails.isLoading
                      ? [1, 2, 3].map((el) => {
                          return (
                            <FormItem
                              className={cn(
                                "flex items-center justify-between gap-1 border-b border-solid border-gray-200",
                                "last:border-b-0 relative"
                              )}
                              key={el}
                            >
                              <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 p-4 h-14 w-full">
                                <Skeleton className="h-5 w-[100px]" />
                              </FormLabel>
                              <FormControl className="!mt-0 right-4 absolute">
                                <Skeleton className="h-5 w-5 rounded-full" />
                              </FormControl>
                            </FormItem>
                          );
                        })
                      : pepDetails.data?.map((el) => {
                          return (
                            <FormItem
                              className={cn(
                                "flex items-center justify-between border-b border-solid border-gray-200",
                                "last:border-b-0 relative"
                              )}
                              key={el.type}
                            >
                              <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer p-4 h-14 w-full">
                                {el.displayText}
                              </FormLabel>
                              <FormControl className="!mt-0 right-4 absolute">
                                <RadioGroupItem value={el.type} />
                              </FormControl>
                            </FormItem>
                          );
                        })}
                  </RadioGroup>
                </FormItem>
              );
            }}
          />
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full relative"
                disabled={!pep.length || loading}
                type="submit"
              >
                {loading ? (
                  <div className="absolute inset-0 justify-center flex items-center w-full">
                    <Loader className="animate-spin" />
                  </div>
                ) : (
                  t("continue")
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default Pep;
